import { useTranslation } from "react-i18next";

import {
  EDITOR_LANGUAGES,
  EDITOR_LANGUAGE_STORAGE_KEY,
  resolveEditorLanguage,
  type EditorLanguage,
} from "./languages";

export interface EditorLanguageControl {
  /** The language in effect, always one of EDITOR_LANGUAGES. */
  current: EditorLanguage;
  languages: EditorLanguage[];
  change: (short: string) => void;
}

/**
 * Reading and writing the editor language, the two lines the language dialog
 * has always done. i18next holds the live value; localStorage is what survives
 * a reload (it is not part of UserSettings, so it does not follow the user to
 * another device).
 */
export function useEditorLanguage(): EditorLanguageControl {
  const { i18n } = useTranslation();

  const short = resolveEditorLanguage(i18n.language ?? null);
  const current =
    EDITOR_LANGUAGES.find((language) => language.short === short) ??
    EDITOR_LANGUAGES[0];

  const change = (next: string) => {
    if (next === current.short) {
      return;
    }
    i18n.changeLanguage(next);
    try {
      localStorage.setItem(EDITOR_LANGUAGE_STORAGE_KEY, next);
    } catch {
      // Storage can throw in private windows. The language still applies for
      // this session, which is better than failing the click.
    }
  };

  return { current, languages: EDITOR_LANGUAGES, change };
}
